import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PROPERTY_TYPES, resolvePropertyType } from "@/lib/propertyTypes";
import PropertyIllustration from "@/components/icons/PropertyIllustrations";
import { useTranslation } from "react-i18next";

/**
 * Categorias na Home — ilustrações premium (line-art coloridas) inspiradas
 * em Airbnb/Booking, em vez de ícones genéricos.
 */

// Paleta dedicada por categoria (cor principal + accent decorativo).
// Mantém harmonia com o resto da plataforma sem cair em monocromia.
const PALETTE: Record<string, { color: string; accent: string; underline: string }> = {
  "Apartamento":   { color: "#3B82F6", accent: "#60A5FA", underline: "#3B82F6" },
  "Hotel":         { color: "#7C3AED", accent: "#A78BFA", underline: "#7C3AED" },
  "Hospedaria":    { color: "#EA580C", accent: "#22C55E", underline: "#EA580C" },
  "Resort":        { color: "#10B981", accent: "#F59E0B", underline: "#10B981" },
  "Quarto T1":     { color: "#EC4899", accent: "#F472B6", underline: "#EC4899" },
  "Contentor":     { color: "#0F766E", accent: "#22C55E", underline: "#0F766E" },
  "Barco":         { color: "#0EA5E9", accent: "#0369A1", underline: "#0EA5E9" },
  "Vivenda":       { color: "#D97706", accent: "#22C55E", underline: "#D97706" },
  "Casa de Campo": { color: "#059669", accent: "#15803D", underline: "#059669" },
  "Casa de Praia": { color: "#06B6D4", accent: "#F59E0B", underline: "#06B6D4" },
  "Guest House":   { color: "#9333EA", accent: "#EC4899", underline: "#9333EA" },
  "Suite Privada": { color: "#C026D3", accent: "#F472B6", underline: "#C026D3" },
};

const Categories = () => {
  const { t } = useTranslation();

  const { data: counts = {} } = useQuery({
    queryKey: ["category-counts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("properties")
        .select("property_type")
        .eq("status", "active");
      if (error) throw error;
      const map: Record<string, number> = {};
      (data || []).forEach((row: any) => {
        const def = resolvePropertyType(row.property_type);
        if (!def) return;
        map[def.value] = (map[def.value] || 0) + 1;
      });
      return map;
    },
    staleTime: 5 * 60 * 1000,
  });

  return (
    <section className="pt-3 pb-8 md:pt-4 md:pb-10 border-b border-border/30">
      <div className="container mx-auto px-4">
        <div className="flex gap-1 md:gap-2 overflow-x-auto pb-3 scrollbar-hide">
          {PROPERTY_TYPES.map((category, index) => {
            const count = counts[category.value] || 0;
            const palette = PALETTE[category.value] || { color: "#6B7280", accent: "#9CA3AF", underline: "#6B7280" };
            return (
              <motion.div
                key={category.value}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03, duration: 0.3 }}
                className="group relative flex flex-col items-center min-w-[110px] md:min-w-[120px] px-2 pt-2 pb-3 rounded-2xl select-none cursor-default"
                aria-label={`${category.labelPlural} (${count})`}
              >
                {/* Illustration */}
                <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center transition-transform group-hover:scale-[1.06]">
                  <PropertyIllustration
                    type={category.value}
                    color={palette.color}
                    accent={palette.accent}
                    className="w-full h-full"
                  />
                </div>

                {/* Label */}
                <span className="mt-1 text-[12.5px] md:text-[13px] font-semibold text-foreground/85 group-hover:text-foreground whitespace-nowrap">
                  {t(`propertyTypes.${category.value}.labelPlural`) || category.labelPlural}
                </span>

                {/* Count + colored underline accent (matches uploaded reference) */}
                <span className="text-[10.5px] text-muted-foreground mt-0.5 tabular-nums">
                  {count > 0 ? count.toLocaleString("pt-AO") : "—"}
                </span>
                <span
                  aria-hidden
                  className="mt-1.5 block h-[2.5px] w-7 rounded-full opacity-70 group-hover:opacity-100 group-hover:w-10 transition-all"
                  style={{ backgroundColor: palette.underline }}
                />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Categories;

